import { useTranslation } from 'react-i18next';
import Layout from '@/components/Layout';
import Reveal from '@/components/Reveal';
import SEO from '@/components/SEO';
import { SectionLabel, PrimaryButton } from '@/components/Button';
import { IMAGES } from '@/data/insights';

export default function About() {
  const { t } = useTranslation(['about', 'common']);
  const story = t('story.paragraphs', { returnObjects: true });
  const believe = t('believe.paragraphs', { returnObjects: true });

  return (
    <Layout>
      <SEO title={t('seo.title')} description={t('seo.description')} />

      <section className="pt-32 md:pt-40 pb-12" data-testid="about-hero">
        <div className="max-w-content mx-auto px-6 md:px-8 max-w-4xl">
          <Reveal>
            <SectionLabel>{t('hero.eyebrow')}</SectionLabel>
            <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-[60px] font-semibold text-brand-primary leading-[1.06] tracking-tight">
              {t('hero.headline')}
            </h1>
          </Reveal>
        </div>
      </section>

      <section className="pb-16 md:pb-20" data-testid="about-banner">
        <div className="max-w-content mx-auto px-6 md:px-8">
          <Reveal delay={0.05}>
            <div className="overflow-hidden aspect-[21/9] relative">
              <img
                src={IMAGES.aboutBanner}
                alt=""
                loading="eager"
                className="w-full h-full object-cover saturate-[0.9]"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/15 to-black/0" aria-hidden="true" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-16 md:py-20" data-testid="about-story">
        <div className="max-w-content mx-auto px-6 md:px-8 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <Reveal>
              <SectionLabel>{t('story.label')}</SectionLabel>
            </Reveal>
          </div>
          <div className="md:col-span-8 max-w-[68ch]">
            <div className="space-y-7">
              {story.map((p, i) => (
                <Reveal key={i} delay={i * 0.03}>
                  <p className="text-[17px] leading-[1.85] text-brand-text">{p}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-brand-surface" data-testid="about-believe">
        <div className="max-w-content mx-auto px-6 md:px-8 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <Reveal>
              <SectionLabel>{t('believe.label')}</SectionLabel>
            </Reveal>
          </div>
          <div className="md:col-span-8 max-w-[68ch]">
            <div className="space-y-7">
              {believe.map((p, i) => (
                <Reveal key={i} delay={i * 0.04}>
                  <p className="text-[17px] leading-[1.85] text-brand-text">{p}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20" data-testid="about-bring">
        <div className="max-w-content mx-auto px-6 md:px-8 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <Reveal>
              <SectionLabel>{t('bring.label')}</SectionLabel>
            </Reveal>
          </div>
          <div className="md:col-span-8 max-w-[68ch]">
            <Reveal delay={0.04}>
              <p className="text-[17px] leading-[1.85] text-brand-text">{t('bring.body')}</p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-brand-surface" data-testid="about-cta">
        <div className="max-w-content mx-auto px-6 md:px-8 max-w-3xl">
          <Reveal>
            <SectionLabel>{t('cta.label')}</SectionLabel>
            <h2 className="mt-5 font-display text-3xl md:text-[44px] font-semibold text-brand-primary leading-tight tracking-tight">
              {t('cta.headline')}
            </h2>
            <p className="mt-6 text-[17px] text-brand-text leading-[1.8] max-w-[68ch]">{t('cta.body')}</p>
            <div className="mt-10">
              <PrimaryButton to="/contact" testId="about-get-in-touch">{t('cta.startConversation', { ns: 'common' })}</PrimaryButton>
            </div>
          </Reveal>
        </div>
      </section>
    </Layout>
  );
}
